import { Injectable } from "@angular/core";
import { HttpClient, HttpEvent, HttpRequest } from "@angular/common/http";
import { Observable } from "rxjs";
import { environment } from "../../../environments/environment";

/** DTO returned by backend */
export interface FileItem {
  fileId: number;
  name: string;
  type: string;
  size: string; // comes as String in the record; parse to number if needed
  uploaded: string; // ISO string (Date in backend); new Date(uploaded) on client if needed
  description: string | null;
}

/** Optional metadata */
export interface UploadMetadata {
  description?: string;
}

@Injectable({ providedIn: "root" })
export class FilesService {
  private filesApi = environment.endpoints.files;
  private uploadApi = environment.endpoints.upload;

  constructor(private http: HttpClient) {}

  /**
   * Uploads a file to the server with the specified metadata.
   *
   * @param {File} file The file to be uploaded.
   * @param {UploadMetadata} meta An object containing additional metadata to be sent with the file, where keys represent the metadata fields and values represent the corresponding values.
   * @return {Observable<HttpEvent<string>>} An Observable that emits HTTP events related to the upload process, such as progress or completion.
   */
  uploadFile(file: File, meta: UploadMetadata): Observable<HttpEvent<string>> {
    const formData = new FormData();
    formData.append("file", file, file.name);
    Object.entries(meta).forEach(([k, v]) => {
      if (v != null) formData.append(k, v as string);
    });

    const req = new HttpRequest("POST", `${this.uploadApi}`, formData, {
      reportProgress: true,
      responseType: "text",
    });
    return this.http.request(req);
  }

  /**
   * Retrieves all files from the specified API endpoint.
   *
   * @return {Observable<FileItem[]>} An observable emitting an array of FileItem objects.
   */
  getAllFiles(): Observable<FileItem[]> {
    return this.http.get<FileItem[]>(`${this.filesApi}`);
  }

  /**
   * Updates the description of a file with the given fileId.
   *
   * @param {number} fileId - The unique identifier of the file to update.
   * @param {string} description - The new description for the file.
   * @return {Observable<FileItem>} An observable containing the updated file item.
   */
  updateFileDescription(
    fileId: number,
    description: string
  ): Observable<FileItem> {
    return this.http.put<FileItem>(
      `${this.filesApi}/${fileId}/description`,
      description,
      { headers: { "Content-Type": "text/plain" } } // backend expects raw String
    );
  }

  /**
   * Deletes a file with the specified file ID from the server.
   *
   * @param {number} fileId - The unique identifier of the file to be deleted.
   * @return {Observable<void>} An observable that completes when the file is successfully deleted.
   */
  deleteFile(fileId: number): Observable<void> {
    return this.http.delete<void>(`${this.filesApi}/${fileId}`);
  }
}
